import React from "react"; // eslint-disable-line no-unused-vars
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CloseIcon from "@mui/icons-material/Close";
import { useDrop } from "react-dnd";
import { DND_ITEM_TYPES } from "../supplyTableRow";

import AddProductSidebar from "../AddProductSidebar/AddProductSidebar";
import HistorySidebarHeader from "../HistorySidebar/HistorySidebarHeader";
import HistorySidebarRow from "../HistorySidebar/HistorySidebarRow";
import CombinedSidebar from "../CombinedSidebar/CombinedSidebar";

const SIDEBAR_WIDTH = 700;
const HEADER_OFFSET = 120;

const RightSidebar = ({
  // Common
  open,
  activeMode, // "addProduct" | "history" | "combined"
  onToggle,
  onSwitchMode,
  onClose,
  // AddProduct props
  onAddProduct,
  tabPosition,
  deliveryDate,
  supplierGroupValue,
  supplyDivisionValue,
  submitVersion,
  addedProductList,
  predictAddedProduct,
  openAddProductMasterDialog,
  openAddProductDetailMasterDialog,
  sidebarRefreshTrigger,
  sidebarScrollToProductId,
  onSidebarScrollComplete,
  // History props
  supplyHistoryAll,
  hidden,
  selected,
  toggleSelect,
  handleBack,
  handleBulkBack,
  handleToggleAllHidden,
  handleHide,
  moveHiddenItem,
  onHiddenRowDragBegin,
  onHiddenRowDragCancel,
}) => {
  // Drop zone: main table rows dragged into history sidebar → hide them
  const [{ isOver: isOverHide, canDrop: canDropHide }, dropHide] = useDrop(
    () => ({
      accept: DND_ITEM_TYPES.PRODUCT_ROW,
      drop: (item) => {
        if (handleHide && item.product) {
          handleHide(item.product);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [handleHide],
  );

  return (
    <>
      {/* Toggle Buttons - stacked vertically on the right edge */}
      <Box
        sx={{
          position: "fixed",
          right: open ? SIDEBAR_WIDTH : 0,
          top: `calc(${HEADER_OFFSET}px + (100vh - ${HEADER_OFFSET}px) / 2)`,
          transform: "translateY(-50%)",
          zIndex: 1200,
          display: "flex",
          flexDirection: "column",
          transition: "right 0.3s ease",
        }}
      >
        {/* 商品検索 toggle (combined) */}
        <Button
          onClick={() => onSwitchMode("combined")}
          variant={
            activeMode === "combined" && open ? "contained" : "outlined"
          }
          startIcon={
            activeMode === "combined" && open ? (
              <CloseIcon />
            ) : (
              <ChevronLeftIcon />
            )
          }
          sx={{
            bgcolor:
              activeMode === "combined" && open ? "success.dark" : "#ffffff",
            borderColor:
              activeMode === "combined" && open ? "success.dark" : "grey.400",
            color:
              activeMode === "combined" && open ? "#ffffff" : "text.primary",
            "&:hover": {
              bgcolor:
                activeMode === "combined" && open ? "success.main" : "#f3fff3",
              borderColor: "success.main",
            },
            boxShadow: 2,
            writingMode: "vertical-rl",
            textOrientation: "mixed",
            py: 2,
            px: 1,
            minWidth: "auto",
            fontSize: 19,
            fontWeight: 600,
            borderRadius: "5px",
            "& .MuiButton-startIcon": { margin: 0, marginBottom: 0.5 },
          }}
        >
          {activeMode === "combined" && open ? "閉じる" : "商品検索"}
        </Button>
      </Box>

      {/* Sidebar Panel */}
      <Box
        sx={{
          position: "fixed",
          right: 0,
          top: HEADER_OFFSET,
          height: `calc(100vh - ${HEADER_OFFSET}px)`,
          width: open ? SIDEBAR_WIDTH : 0,
          overflow: "hidden",
          transition: "width 0.3s ease",
          zIndex: 1100,
          border : "solid",
          borderColor: "black",
          borderWidth: 1,
          borderRadius:3,
        }}
      >
        <Paper
          elevation={4}
          sx={{
            width: SIDEBAR_WIDTH,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            borderRadius: 0,
          }}
        >
          {/* ── AddProduct mode ────────────────────────────────────── */}
          {activeMode === "addProduct" && (
            <AddProductSidebar
              open={open}
              activeMode={activeMode}
              tabPosition={tabPosition}
              addedProductList={addedProductList}
              predictAddedProduct={predictAddedProduct}
              onAddProduct={onAddProduct}
              onAddProductMaster={openAddProductMasterDialog}
              onAddProductDetailMaster={openAddProductDetailMasterDialog}
              onDragBegin={onHiddenRowDragBegin}
              onDragCancel={onHiddenRowDragCancel}
              onClose={onClose}
              refreshTrigger={sidebarRefreshTrigger}
              scrollToProductId={sidebarScrollToProductId}
              onScrollComplete={onSidebarScrollComplete}
            />
          )}

          {/* ── Combined mode ──────────────────────────────────────── */}
          {activeMode === "combined" && (
            <CombinedSidebar
              open={open}
              activeMode={activeMode}
              tabPosition={tabPosition}
              deliveryDate={deliveryDate}
              supplierGroupValue={supplierGroupValue}
              supplyDivisionValue={supplyDivisionValue}
              submitVersion={submitVersion}
              addedProductList={addedProductList}
              predictAddedProduct={predictAddedProduct}
              onAddProduct={onAddProduct}
              onAddProductMaster={openAddProductMasterDialog}
              onAddProductDetailMaster={openAddProductDetailMasterDialog}
              onDragBegin={onHiddenRowDragBegin}
              onDragCancel={onHiddenRowDragCancel}
              onClose={onClose}
              refreshTrigger={sidebarRefreshTrigger}
              scrollToProductId={sidebarScrollToProductId}
              onScrollComplete={onSidebarScrollComplete}
              hidden={hidden}
              supplyHistoryAll={supplyHistoryAll}
              handleBack={handleBack}
            />
          )}

          {/* ── History mode ───────────────────────────────────────── */}
          {activeMode === "history" && (
            <Box
              ref={dropHide}
              sx={{
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                bgcolor:
                  isOverHide && canDropHide ? "action.hover" : "transparent",
                border: canDropHide ? "2px dashed" : "2px solid transparent",
                borderColor:
                  isOverHide && canDropHide
                    ? "primary.main"
                    : canDropHide
                      ? "grey.300"
                      : "transparent",
                borderRadius: 1,
                transition: "all 0.2s ease",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
                  過去に発注した商品 ({hidden?.length || 0})
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                  onClick={onClose}
                  sx={{
                    fontSize: 12,
                    py: 0.5,
                    px: 1.5,
                    minWidth: "auto",
                    color: "grey.600",
                    borderColor: "grey.400",
                    "&:hover": {
                      color: "error.main",
                      borderColor: "error.main",
                      bgcolor: "error.50",
                    },
                  }}
                >
                  閉じる
                </Button>
              </Box>

              <Divider sx={{ mb: 1 }} />

              {/* Bulk actions */}
              {/* <Grid container spacing={1} sx={{ mb: 1 }} justifyContent="flex-end">
                <Grid item>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleToggleAllHidden}
                    sx={{ fontSize: "0.75rem", py: 0.25 }}
                  >
                    {selected?.hidden?.length === hidden?.length ? "全解除" : "全選択"}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={!selected?.hidden?.length}
                    onClick={handleBulkBack}
                    sx={{ fontSize: "0.75rem", py: 0.25 }}
                  >
                    一括表示
                  </Button>
                </Grid>
              </Grid> */}

              <HistorySidebarHeader />

              <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
                {!hidden?.length ? (
                  <Typography
                    color="text.secondary"
                    sx={{ py: 2, fontSize: "0.8rem" }}
                  >
                    非表示の商品はありません。
                  </Typography>
                ) : (
                  hidden.map((rep, index) => (
                    <HistorySidebarRow
                      key={rep._groupKey}
                      product={rep}
                      index={index}
                      selected={selected?.hidden?.includes(rep._groupKey)}
                      onSelect={() => toggleSelect(rep, true)}
                      onShow={() => handleBack(rep)}
                      onMoveItem={moveHiddenItem}
                      onDragBegin={onHiddenRowDragBegin}
                      onDragCancel={onHiddenRowDragCancel}
                    />
                  ))
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default RightSidebar;
